import React, { useState, useMemo, useEffect } from 'react';
import { useNotifications } from 'features/notifications';
import { formatDistanceToNow } from 'date-fns';
import { useSocket } from 'providers/SocketProvider';

const TABS = ['transaction', 'primary', 'system', 'updates'];

const NotificationCard = ({ notif }) => (
  <div
    key={notif.id}
    className="p-3 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-all space-y-1"
  >
    <div className="flex justify-between items-center">
      <p className="font-medium">{notif.title}</p>
      <span className="text-xs text-white/50">
        {formatDistanceToNow(new Date(notif.createdAt), { addSuffix: true })}
      </span>
    </div>
    <p className="text-sm text-white/80">{notif.message}</p>
    {notif.transaction?.id && (
      <p className="text-xs text-white/60">
        Transaction ID: <span className="font-mono">{notif.transaction.id}</span>
      </p>
    )}
  </div>
);

const Pagination = ({ page, onPrev, onNext, isDisabled }) => (
  <div className="flex justify-between mt-4 text-white/80 text-sm">
    <button
      onClick={onPrev}
      disabled={page === 1}
      className="hover:underline disabled:opacity-30"
    >
      Previous
    </button>
    <span>Page {page}</span>
    <button
      onClick={onNext}
      disabled={isDisabled}
      className="hover:underline disabled:opacity-30"
    >
      Next
    </button>
  </div>
);

export const RecentNotifications = () => {
  const [type, setType] = useState('transaction');
  const [pageNumber, setPageNumber] = useState(1);
  const [localNotifications, setLocalNotifications] = useState([]);
  const { data, isLoading } = useNotifications({ filter: 'recent', type, pageNumber });
  const { socket } = useSocket();

  const remoteNotifications = useMemo(() => data?.notifications ?? [], [data]);

  // Update local state when API data loads
  useEffect(() => {
    if (remoteNotifications.length > 0) {
      setLocalNotifications(remoteNotifications);
    }
  }, [remoteNotifications]);

  // Handle real-time socket updates
  useEffect(() => {
    if (!socket) return;

    const handleSocketUpdate = (eventName, payload) => {
      const newItems = Array.isArray(payload?.data) ? payload.data : [];

      const mapped = newItems.map((n) => ({
        id: n.id || crypto.randomUUID(),
        title: n.title || 'New Notification',
        message: n.description || 'You have a new alert',
        createdAt: n.createdAt || new Date().toISOString(),
        transaction: n.transaction || null,
      }));

      setLocalNotifications((prev) => {
        const seen = new Set();
        const merged = [...mapped, ...prev];
        return merged.filter((n) => {
          const id = n.id || n.transaction?.id;
          if (!id || seen.has(id)) return false;
          seen.add(id);
          return true;
        });
      });
    };

    socket.onAny(handleSocketUpdate);
    return () => {
      socket.offAny(handleSocketUpdate);
    };
  }, [socket]);

  return (
    <div className="text-left">
      <h3 className="text-white font-semibold mb-3 text-lg">Recent Notifications</h3>

      {/* Tabs */}
      <div className="flex gap-1 mb-4">
        {TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => {
              setType(tab);
              setPageNumber(1);
            }}
            className={`px-2 py-1 rounded-full text-xs transition-all ${
              type === tab
                ? 'bg-white/20 text-white font-semibold'
                : 'bg-white/10 text-white/60 hover:bg-white/15'
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="space-y-3">
        {isLoading ? (
          Array.from({ length: 3 }).map((_, idx) => (
            <div key={idx} className="w-full h-[64px] rounded-xl bg-white/10 animate-pulse" />
          ))
        ) : localNotifications.length === 0 ? (
          <p className="text-sm text-white/60">You have no notifications.</p>
        ) : (
          localNotifications.map((notif) => (
            <NotificationCard key={notif.id} notif={notif} />
          ))
        )}
      </div>

      {/* Pagination */}
      {!isLoading && localNotifications.length > 0 && (
        <Pagination
          page={pageNumber}
          onPrev={() => setPageNumber((prev) => Math.max(prev - 1, 1))}
          onNext={() => setPageNumber((prev) => prev + 1)}
          isDisabled={remoteNotifications.length < 5}
        />
      )}
    </div>
  );
};
